// Wrapper (overflow container). A fractional width lands the table's column
// boundaries off the device grid and blurs small crisp cell glyphs (the xs
// StatusIndicator), so round it down to a whole pixel where `round()` is
// supported. It lives here, not on the <table>, so a caller's width still wins.
export const tableWrapperClasses =
  'scrollbar-subtle relative w-full supports-[width:round(down,100%,1px)]:w-[round(down,100%,1px)] overflow-auto';

export const tableClasses = 'group/table w-full caption-bottom text-sm';

// TableHeader (<thead>).
// No row-level hover: header highlighting is per-cell, applied by the
// interactive header (`DataTableColumnHeader` button) so only the hovered
// cell changes — not the whole row.
export const tableHeaderClasses = [
  '[&_tr]:border-b [&_tr]:bg-surface-accent',
  // List header defers to the surface behind it (transparent) so it matches its
  // container in every theme, like body rows, which are transparent at rest. A
  // sticky header would opt back into an opaque fill.
  'group-data-[variant=list]/table:[&_tr]:border-0 group-data-[variant=list]/table:[&_tr]:bg-transparent',
].join(' ');

// TableBody (<tbody>).
export const tableBodyClasses = '[&_tr:last-child]:border-0';

// TableFooter (<tfoot>).
export const tableFooterClasses = [
  'border-t bg-background-secondary/50 font-medium [&>tr]:last:border-b-0',
  'group-data-[variant=list]/table:border-dt-border group-data-[variant=list]/table:bg-dt-footer',
].join(' ');

// TableRow (<tr>). The `cursor-pointer` toggle (driven by `onClick`) is applied
// in the component, after these base classes and before the user `className`.
export const tableRowClasses = [
  'border-b bg-surface transition-colors hover:bg-surface-hover data-[state=selected]:bg-surface-selected data-[state=selected]:hover:bg-surface-selected-hover',
  // Rows stay transparent; fill is exposed via `--dt-fill` and painted by
  // the cells (`bg-[var(--dt-fill,transparent)]`, pinned cells
  // `bg-[var(--dt-fill,var(--dt-pinned-bg))]`). Routing through cells lets
  // selected rows clip to rounded corners in `border-separate` mode.
  //
  // Leave `--dt-fill` UNDEFINED at rest — do NOT set it to `transparent`.
  // `var()` only falls back on undefined, not on defined-as-transparent,
  // so an explicit transparent kills the pinned cell's `--dt-pinned-bg`
  // fallback and the pinned column goes see-through.
  'group-data-[variant=list]/table:border-0 group-data-[variant=list]/table:bg-transparent group-data-[variant=list]/table:transition-none group-data-[variant=list]/table:hover:[--dt-fill:var(--dt-row-bg-hover)] group-data-[variant=list]/table:data-[state=selected]:[--dt-fill:var(--dt-row-bg-selected)] group-data-[variant=list]/table:data-[state=selected]:hover:[--dt-fill:var(--dt-row-bg-selected-hover)]',
  // Active fill (data-active=true) marks the currently-viewed row.
  // Compound rules give hover and selected higher specificity, so they
  // dominate over the sand active fill when they apply.
  'group-data-[variant=list]/table:data-[active=true]:[--dt-fill:var(--dt-row-bg-active)]',
  'group-data-[variant=list]/table:data-[active=true]:hover:[--dt-fill:var(--dt-row-bg-hover)]',
  'group-data-[variant=list]/table:data-[active=true]:data-[state=selected]:[--dt-fill:var(--dt-row-bg-selected)]',
  'group-data-[variant=list]/table:data-[active=true]:data-[state=selected]:hover:[--dt-fill:var(--dt-row-bg-selected-hover)]',
  'group-data-[striped]/table:even:[--dt-fill:hsl(var(--twc-surface-page))] group-data-[striped]/table:even:hover:[--dt-fill:var(--dt-row-bg-hover)]',
  'group-data-[striped]/table:data-[state=selected]:[--dt-fill:var(--dt-row-bg-selected)] group-data-[striped]/table:data-[state=selected]:hover:[--dt-fill:var(--dt-row-bg-selected-hover)]',
].join(' ');

// TableHead (<th>). `typographyVariant` and the `cursor-pointer` toggle stay in
// the component; these are the static classes applied to the cell.
export const tableHeadClasses = 'm-0 p-0';

// Inner layout div rendered inside every <th>.
export const tableHeadInnerClasses = [
  // `:not([class*='size-'])` lets descendants (e.g. StatusIndicator's
  // colour-locked glyph) keep their own prescribed size — bare lucide
  // icons still default to 14px.
  'flex size-fit items-center gap-1.5 whitespace-nowrap px-4 py-2.5 text-start align-middle text-text-primary [&:has([role=checkbox])]:py-[9px] [&_svg:not([class*=size-])]:size-3.5',
  // List header type: colour goes through the `dt-header-text` alias token (one
  // themeable place, like the other dt- colours); `text-xs font-normal` is the
  // `caption` typography variant, overriding the `p2Bold` the <th> applies.
  'group-data-[variant=list]/table:text-dt-header-text group-data-[variant=list]/table:font-normal group-data-[variant=list]/table:text-xs',
  'group-data-[variant=list]/table:min-h-8 group-data-[variant=list]/table:p-2 group-data-[variant=list]/table:[&:has([role=checkbox])]:py-2',
].join(' ');

// TableCell (<td>). `typographyVariant` stays in the component.
export const tableCellClasses = [
  'm-0 min-h-fit p-0 align-middle',
  // Paint the row fill on the cell (driven by the <tr>'s `--dt-fill` var) so
  // it can be clipped to rounded corners. Every filled row (hover / active /
  // a lone selection) reads as a pill via the base radius below; a run of
  // adjacent selected rows squares its joining corners via
  // `dataTableSelectionRoundingClasses`. On unfilled rows the fill is
  // transparent, so the radius is a harmless no-op.
  'group-data-[variant=list]/table:bg-[var(--dt-fill,transparent)] group-data-[variant=list]/table:first:rounded-s-md group-data-[variant=list]/table:last:rounded-e-md',
].join(' ');

// Middle padding div rendered inside every <td>.
export const tableCellContentClasses = [
  'min-h-fit overflow-hidden px-4 py-[9px]',
  'group-data-[variant=list]/table:p-2',
].join(' ');

// Innermost flex div rendered inside every <td> (also receives the user
// `className`, mirroring the original implementation).
export const tableCellFlexClasses = [
  'flex min-h-[22px] min-w-0 items-center gap-1.5 [&_svg:not([class*=size-])]:size-3.5',
  'group-data-[variant=list]/table:min-h-8',
].join(' ');

// TableCaption (<caption>).
export const tableCaptionClasses = [
  'mt-4 text-sm text-text-secondary',
  'group-data-[variant=list]/table:text-dt-caption-text',
].join(' ');
